import React, { useEffect, useState, useMemo } from "react";
import Layout from "@/components/wrappers/layouts/main-layout/Layout";
import { useDispatch, useSelector } from "react-redux";
import { updateTitle } from "@/features/titleSlice";
import { updateActiveModule } from "@/features/activeModuleSlice";
import { getEmail, getUsername, getUserRole } from "@services/UserService";
import { v1 as uuidv1 } from "uuid";
import { SignJWT } from "jose";
import { gzip } from "pako";

const OneDer: React.FC = () => {
    const dispatch = useDispatch();
    const clientSubGroupId = useSelector(
        (state: any) => state.clientSubGroupId.value
    );

    useEffect(() => {
        dispatch(updateTitle("1der ai"));
        dispatch(updateActiveModule("1der"));
        // eslint-disable-next-line
    }, []);

    const baseUrl = process.env.VITE_1DER_URL || "";
    const [iframeSrc, setIframeSrc] = useState<string>(baseUrl);
    // const [isLoading, setIsLoading] = useState<boolean>(true);

    // Storage key for caching the token data
    const CACHE_KEY = "1der_iframe_cache";

    // Memoize user info to detect when it actually changes
    const userInfo = useMemo(() => ({
        name: getUsername() || "",
        email: getEmail(),
        role: getUserRole(),
        clientSubGroupId: clientSubGroupId ? Number(clientSubGroupId) : null,
    }), [clientSubGroupId]);

    useEffect(() => {
        const buildAndSetUrl = async () => {
            // setIsLoading(true);
            try {
                if (!baseUrl) {
                    setIframeSrc("");
                    return;
                }

                const secret = process.env.VITE_1DER_SECRET_KEY;
                if (!secret || !clientSubGroupId) {
                    setIframeSrc(baseUrl);
                    return;
                }

                // Try to get cached data from sessionStorage
                //TODO[DND]: This is a workaround @prajwal has to change the way he gets data for the chats, for time being this has been added
                const cachedData = sessionStorage.getItem(CACHE_KEY);
                if (cachedData) {
                    try {
                        const parsed = JSON.parse(cachedData);
                        // Check if cached data matches current user info
                        if (parsed.name === userInfo.name &&
                            parsed.email === userInfo.email &&
                            parsed.role === userInfo.role &&
                            parsed.clientSubGroupId === userInfo.clientSubGroupId &&
                            parsed.url) {
                            // Reuse the cached URL
                            setIframeSrc(parsed.url);
                            return;
                        }
                    } catch {
                        // Invalid cache, continue to regenerate
                    }
                }

                // Generate new URL only when necessary
                const payload = {
                    name: userInfo.name,
                    client_subgroup_id: userInfo.clientSubGroupId!,
                    email: userInfo.email,
                    role: userInfo.role,
                    uuid: uuidv1(),
                } as const;

                const token = await new SignJWT(payload)
                    .setProtectedHeader({ alg: "HS256", typ: "JWT" })
                    .setIssuedAt()
                    .setExpirationTime("12h")
                    .sign(new TextEncoder().encode(secret));

                const compressedBytes = gzip(token);

                const toBase64Url = (bytes: Uint8Array): string => {
                    let binary = "";
                    for (let i = 0; i < bytes.length; i++) {
                        binary += String.fromCharCode(bytes[i]);
                    }
                    const base64 = btoa(binary);
                    return base64;
                };

                const compressedToken = toBase64Url(compressedBytes);
                const url = new URL(baseUrl);
                url.searchParams.set("token", compressedToken);
                const finalUrl = url.toString();

                // Cache the URL with the current user info in sessionStorage
                sessionStorage.setItem(CACHE_KEY, JSON.stringify({
                    url: finalUrl,
                    ...userInfo,
                }));

                setIframeSrc(finalUrl);
            } catch {
                setIframeSrc(baseUrl);
            }
        };

        buildAndSetUrl();
        // eslint-disable-next-line
    }, [baseUrl, userInfo]);

    return (
        <Layout isHeaderRequired={false} isFooterRequired={false}>
            <div
                className="main-content-body"
                style={{ padding: 0, position: "relative" }}
            >
                {/* {isLoading && (
                    <div
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100vh",
                            zIndex: 10,
                        }}
                    >
                        <AgGridLoaderComponent className="w-100" />
                    </div>
                )} */}
                <iframe
                    title="1DER"
                    src={iframeSrc}
                    style={{
                        width: "100%",
                        height: "100vh",
                        border: 0,
                        background: "#fff",
                    }}
                    allow="clipboard-write; clipboard-read; fullscreen;"
                    // onLoad={() => setIsLoading(false)}
                />
            </div>
        </Layout>
    );
};

export default OneDer;